import React from 'react';
import { Menu, MenuItem, CircularProgress, IconButton } from '@mui/material';
import { ALL_TODO_INTEGRATIONS_TYPE, TodoIntegrationType } from '@demind-inc/core';
import './ConnectTodosDropdown.scss';
import {
  AVAILABLE_TODO_INTEGRATIONS_TYPE,
  CannyPostOptionnName,
  visibleFeedbackDialogPostNameAtom,
} from '../../../data-access';
import { TODO_INTEGRATION_ICONS } from '../constants';
import { Close, Check, Add, OpenInFull } from '@mui/icons-material';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import { useFeatureFlag } from '../../../utils';
import { isAppleRemindersDesktopSupported } from '../../../helpers';
import { useConnectAppleReminders } from '../../../hooks';
import { useTranslation } from 'react-i18next';

interface ConnectTodosDropdownProps {
  anchorEl: HTMLElement | null;
  generatingTodoAuth?: TodoIntegrationType;
  onClickItem: (todoType: TodoIntegrationType) => void;
  onClose: () => void;
  connectedTodos?: Array<{ source: TodoIntegrationType }>;
}

const ConnectTodosDropdown: React.FC<ConnectTodosDropdownProps> = ({
  anchorEl,
  generatingTodoAuth,
  onClickItem,
  onClose,
  connectedTodos,
}) => {
  const { t } = useTranslation();
  const [_, setVisibleFeedbackPostName] = useRecoilState(visibleFeedbackDialogPostNameAtom);
  const { featureFlags } = useFeatureFlag();
  const { connectAppleReminders, isLoading: isConnectingAppleReminders } =
    useConnectAppleReminders();
  const isAppleRemindersSupported = isAppleRemindersDesktopSupported();

  const isConnected = (todoType: TodoIntegrationType) =>
    connectedTodos?.some(({ source }) => source === todoType);
  const isComingSonn = (todoType: TodoIntegrationType) =>
    !AVAILABLE_TODO_INTEGRATIONS_TYPE.includes(todoType) ||
    (todoType === 'notion' && !featureFlags.FF_notion_integ) ||
    (todoType === 'github' && !featureFlags.FF_github_integrations) ||
    (todoType === 'google-tasks' && !featureFlags.FF_google_tasks);
  const isBetaIntegration = (todoType: TodoIntegrationType) =>
    todoType === 'google-tasks' || todoType === 'clickup';

  const renderMenuItemContent = (todoType: TodoIntegrationType) => (
    <>
      <div className="connect-todos-dropdown__item__text">
        {isComingSonn(todoType) && (
          <>
            <OpenInFull fontSize="inherit" />
            {t('task.todoIntegration.comingSoon')}
          </>
        )}
        {!isComingSonn(todoType) && isConnected(todoType) && (
          <>
            <Check fontSize="inherit" />
            {t('task.todoIntegration.connected')}
          </>
        )}
        {!isComingSonn(todoType) && !isConnected(todoType) && (
          <>
            <Add fontSize="inherit" />
            {t('task.todoIntegration.connect')}
          </>
        )}
        {isBetaIntegration(todoType) && (
          <span className="connect-todos-dropdown__beta">{t('task.todoIntegration.beta')}</span>
        )}
      </div>
      {generatingTodoAuth === todoType ? (
        <CircularProgress size={15} />
      ) : (
        <img
          src={TODO_INTEGRATION_ICONS[todoType]}
          alt={todoType}
          className="connect-todos-dropdown__icon"
        />
      )}
    </>
  );

  const handleClickItem = async (todoType: TodoIntegrationType) => {
    if (isComingSonn(todoType)) {
      const postName = {
        todoist: null,
        trello: null,
        ticktick: 'support_ticktick',
        clickup: null,
        notion: 'support_notion',
        'google-tasks': null,
      }[todoType];
      setVisibleFeedbackPostName(postName as CannyPostOptionnName | null);
      return;
    }
    if (todoType === 'apple-reminders' && isAppleRemindersSupported) {
      await connectAppleReminders();
      return;
    }
    onClickItem(todoType);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      className="connect-todos-dropdown"
    >
      <div className="connect-todos-dropdown__header">
        <p>{t('task.todoIntegration.connectTodos')}</p>
        <IconButton
          onClick={onClose}
          className="connect-todos-dropdown__close-button"
          aria-label={t('task.common.close')}
        >
          <Close fontSize="small" className="connect-todos-dropdown__close-button__icon" />
        </IconButton>
      </div>
      <div className="connect-todos-dropdown__content">
        {ALL_TODO_INTEGRATIONS_TYPE.map((todoType) => {
          const isAppleRemindersUnsupported =
            todoType === 'apple-reminders' && !isAppleRemindersSupported;
          const isDisabled =
            !!generatingTodoAuth ||
            isConnected(todoType) ||
            isAppleRemindersUnsupported ||
            (todoType === 'apple-reminders' && isConnectingAppleReminders);

          return (
            <MenuItem
              key={todoType}
              onClick={() => handleClickItem(todoType)}
              disabled={isDisabled}
              className={clsx('connect-todos-dropdown__item', {
                'connect-todos-dropdown__item--connected': isConnected(todoType),
              })}
            >
              {renderMenuItemContent(todoType)}
            </MenuItem>
          );
        })}
      </div>
    </Menu>
  );
};

export default ConnectTodosDropdown;
